import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Container,
    Stack,
    Box,
    useTheme,
} from '@mui/material';

import ActionBar from '../components/ActionBar';
import AlertBar from '../components/AlertBar';
import PromptBar from '../components/PromptBar';
import LoadingSpinner from '../components/LoadingSpinner';
import AbstractBackground from '../components/AbstractBackground';
import EmptyStateTiles from '../components/EmptyStateTiles';
import MessageRenderer from '../services/messageRenderer';
import PromptBarRibbon from '../components/PromptBarRibbon';
import Footer from '../components/Footer';

import actionBarConfig from '../config/actionBarConfig';
import promptBarConfig from '../config/promptBarConfig';
import EmptyStateTilesConfig from '../config/emptyStateTilesConfig';

import {
    addMessage,
    clearMessages,
    selectChatContext,
} from '../reducers/messagesReducer';
import {
    setLoading,
} from '../reducers/loadingReducer';
import {
    setShowTiles,
    setIsActiveChat,
    setSelectedTileId,
    clearSelectedTileId,
    clearPromptItems,
    clearSelectedPromptItem,
    hideToast,
} from '../reducers/uiReducer';
import {
    clearSelectedDocument,
} from '../reducers/documentsReducer';
import {
    clearSelectedDataset,
} from '../reducers/datasetsReducer';
import {
    setPromptBarActive,
} from '../reducers/promptBarReducer';

import handleEnterPress from '../services/handleEnterPress';

const Platform = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const containerRef = useRef(null);
    const stackRef = useRef(null);

    const loading = useSelector((state) => state.loading);
    const toast = useSelector((state) => state.ui.toast);
    const messages = useSelector((state) => state.messages.chat_prompt || []);
    const chatContext = useSelector(selectChatContext);
    const { showTiles, isActiveChat, selectedTileId, promptItems } = useSelector((state) => state.ui);
    const [paperHeight, setPaperHeight] = useState(115);

    const handleEnterPressWrapper = async (text, context = null) => {
        await handleEnterPress(text, context, dispatch, chatContext, promptItems);
    };

    const handleNewChat = () => {
        dispatch(clearMessages());
        dispatch(clearSelectedTileId());
        dispatch(setShowTiles(false));
        dispatch(setIsActiveChat(true));
        dispatch(setPromptBarActive(true));
        dispatch(clearSelectedDocument());
        dispatch(clearSelectedDataset());
        dispatch(clearSelectedPromptItem());
        dispatch(clearPromptItems());
    };

    const handleShowTiles = (show) => {
        if (show) {
            dispatch(clearSelectedTileId());
            dispatch(setShowTiles(true));
            dispatch(setIsActiveChat(false));
        } else {
            dispatch(setShowTiles(false));
            dispatch(setIsActiveChat(true));
        }
    };

    const handleSelectTile = (tile) => {
        if (tile.tileType === 'NewChat') {
            handleNewChat();
        } else {
            dispatch(setSelectedTileId(tile.id));
            dispatch(setShowTiles(true));
        }
    };

    const previousScrollHeight = useRef(0);

    useEffect(() => {
        const container = stackRef.current;
        if (!container) return;

        const addedHeight = container.scrollHeight - previousScrollHeight.current;

        // ✅ Check if new content fits in view
        const willOverflowView = addedHeight > container.clientHeight;

        if (!willOverflowView) {
            container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth',
            });
        }

        // Update reference for next time
        previousScrollHeight.current = container.scrollHeight;
    }, [messages]);

    const memoizedBackground = useMemo(
        () => <AbstractBackground animate={loading} containerRef={containerRef} />,
        [loading]
    );

    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                bgcolor: theme.palette.background.default,
            }}
        >
            {memoizedBackground}
            <Box
                sx={{
                    position: 'absolute', // anchors absolute background
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    minWidth: '300px',
                    maxWidth: '1000px',
                }}
            >


                {/* ActionBar */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        bgcolor:
                            theme.palette.mode === 'dark'
                                ? 'rgba(48,48,48,0.9)'
                                : 'rgba(240,240,240,0.9)',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <ActionBar config={actionBarConfig} onShowTiles={handleShowTiles} />
                </Box>

                {/* Main Content Area */}
                <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        overflow: 'hidden', // keep layout contained
                        background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(to top,rgba(18, 18, 18, 0.8),rgba(29, 29, 29, 0.6))'
                            : 'linear-gradient(to top, rgba(255,255,255,0.2), rgba(0,116,211,0.1))',
                    }}
                >
                    <AlertBar
                        open={toast.open}
                        onClose={() => dispatch(hideToast())}
                        severity={toast.severity}
                        message={toast.message}
                    />

                    {(showTiles && !isActiveChat) || selectedTileId ? (
                        <EmptyStateTiles
                            config={EmptyStateTilesConfig}
                            setPromptCallBack={handleEnterPressWrapper}
                            onSelectTile={handleSelectTile}
                            selectedTileId={selectedTileId}
                        />
                    ) : (
                        <Stack
                            ref={stackRef}
                            sx={{
                                flexGrow: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                                p: 2,
                                gap: 2,
                            }}
                        >
                            {Array.isArray(messages) ? (
                                messages.map((message, index) => (
                                    <MessageRenderer
                                        key={index}
                                        message={message}
                                        index={index}
                                    />
                                ))
                            ) : (
                                <p>Error: Messages is not an array</p>
                            )}
                            <Box>{loading && <LoadingSpinner />}</Box>
                        </Stack>
                    )}

                    {(isActiveChat || selectedTileId) && (
                        <Box
                            sx={{
                                bgcolor:
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(28, 28, 30, 0.8)'
                                        : 'rgb(233, 244, 255)',
                                borderTop: `1px solid ${theme.palette.divider}`,
                                p: 1.5,
                            }}
                        >
                            <PromptBarRibbon
                                config={EmptyStateTilesConfig}
                                setPromptCallBack={handleEnterPressWrapper}
                                handleOpen={handleSelectTile}
                            />
                        </Box>
                    )}
                </Box>

                {/* PromptBar and Footer */}
                <Box
                    sx={{
                        height: `${paperHeight}px`,
                        flexShrink: 0,
                        transition: 'height 0.3s ease-in-out',
                    }}
                >
                    <PromptBar
                        config={promptBarConfig}
                        callBack={handleEnterPressWrapper}
                        paperHeight={paperHeight}
                        setPaperHeight={setPaperHeight}
                    />
                </Box>
                <Footer sx={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} />
            </Box>
        </Box>
    );
};

export default Platform;
